import React, { useEffect, useState } from 'react';
import {MixedContentTabBar} from '@bizzabo/arena-components';
import {QnaWebIcon, ChatWebIcon} from '@bizzabo/icons';
import buildTheme from './sessionCollaboration.theme';
import useQna from './useQna';
import {isMobile, providerTypes} from '../../sessionBroadcastUtils';
import {VimeoChat} from '../../VirtualSessions/Vimeo';
import {TransitionWithUnmount} from './TransitionWithUnmount';
import webcastingType from '../../../../../web-common/scripts/enums/webcastingTypeEnum';
import Qna from './Qna';
import FeatureToggleUtils from 'FeatureToggleUtils';

const tabConfigBySession = {
    [providerTypes.VIMEO]: {
        chat: {
            width: 400,
        }
    },
    default: {},
};

const containerConfigBySession = {
    [webcastingType.NEWROW]: {
        initiallyHidden: true
    },
    default: {}
};

const providersWithBuiltinChat = [
    providerTypes.LIVESTREAM,
];

const TAB_INDEX = {
    qna: 0,
    chat: 1,
};

const renderChat = ({provider, virtualSessionDetails}) => {
    const content = {
        [providerTypes.VIMEO]: <VimeoChat {...virtualSessionDetails}/>,
        default: {}
    };
    const safeProvider = content[provider] ? provider : 'default';
    return content[safeProvider];
};

const isProviderHasChat = virtualSessionDetails => {
    if (!virtualSessionDetails) {
        return false;
    }
    const { providerType, chatEnabled} = virtualSessionDetails;
    return chatEnabled && (providerType === providerTypes.VIMEO || providerType === providerTypes.LIVESTREAM);
};

const SessionCollaboration = ({
    event, session, user, siteConf, virtualSessionDetails, isAuthenticated,
    sessionStarted, sessionEnded, showEmailLogin, availableTabs = {ringEngagementPanel: true},
}) => {
    const theme = buildTheme(siteConf);
    const isMobileDevice = isMobile();
    const [isNewQuestion, setIsNewQuestion] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    let qnaProps;
    const isRingQna = FeatureToggleUtils.isFeatureEnabled('newQnaModule');
    const disableNewQnaModule = FeatureToggleUtils.isFeatureEnabled('disableNewQnaModule');
    const { color1 } = siteConf;

    if ((!isRingQna || disableNewQnaModule) && isAuthenticated) {
        const {questions, createQuestion, getQuestions, upvoteQuestion, cancelUpvoteQuestion, loading, error, settings} = useQna(event, session, user);
        const actions = {
            create: createQuestion,
            get: getQuestions,
            upvote: upvoteQuestion,
            cancelUpvote: cancelUpvoteQuestion,
        };
        const data = {questions, user, sessionStarted, sessionEnded, isMobile: isMobileDevice};
        useEffect(() => {
            setIsNewQuestion(true);
        }, [JSON.stringify(questions)]);
        qnaProps = {data, loading, theme, actions, settings, error};
    }

    const virtualSessionType = virtualSessionDetails?.type;
    const safeType = tabConfigBySession[virtualSessionType] ? virtualSessionType : 'default';
    const safeTypeContainer = containerConfigBySession[virtualSessionType] ? virtualSessionType : 'default';
    const provider = virtualSessionDetails?.providerType;
    const safeProvider = tabConfigBySession[provider] ? provider : 'default';

    const login = e => {
        if (!isAuthenticated && showEmailLogin) {
            showEmailLogin();
            e.preventDefault();
            e.stopPropagation();

            return false;
        }
    };
    const {ringEngagementPanel, chat} = availableTabs;
    const width = isMobileDevice ? '100%' : 310;
    const tabs = [];

    if (ringEngagementPanel) {
        const providerHasChat = isProviderHasChat(virtualSessionDetails);

        if (isAuthenticated && isMobileDevice) {
            return <div className="qna-mobile"><Qna {...qnaProps} session={session} event={event} mainColor={color1} providerHasChat={providerHasChat}/></div>;
        }

        if (isRingQna && !disableNewQnaModule && !providerHasChat) {
            return (<div className="qna-without-chat" > <Qna {...qnaProps} session={session} event={event} mainColor={color1} providerHasChat={providerHasChat} /></div>);
        }

        const tabLabel = <>
           <span className="circle-new-message" hidden={!isNewQuestion || tabIndex === TAB_INDEX.qna} />
           <QnaWebIcon height={20} width={20} onClick={login} />
        </>;

        tabs.push({
            label: tabLabel,
            tabContent: isAuthenticated && <Qna {...qnaProps} session={session} event={event} mainColor={color1} providerHasChat={providerHasChat} />,
            text: 'Q&A',
            elevate: false,
            onShow: () =>{
                setTabIndex(TAB_INDEX.qna);
            },
            onHide: () => {
                setTabIndex(-1);
                setIsNewQuestion(false);
            },
            width,
            ...tabConfigBySession[safeType],
            ...tabConfigBySession[safeProvider].qna,
        });
    }
    if (chat && !providersWithBuiltinChat.includes(provider)) {
        tabs.push({
            label: <ChatWebIcon height={20} width={20} onClick={login}/>,
            tabContent: isAuthenticated && renderChat({provider, virtualSessionDetails}),
            text: 'Chat',
            elevate: false,
            width,
            ...tabConfigBySession[safeType],
            ...tabConfigBySession[safeProvider].chat,
        });
    }

    const tabBarProps = {tabs, theme};
    return <TransitionWithUnmount
        mountedStyle="qna mounted"
        unmountedStyle="qna unmounted"
        delayTime={500}
        renderToggle={qnaProps?.settings?.enableQuestionSubmission || (isRingQna && !disableNewQnaModule)}
    >
        <MixedContentTabBar
            toggleTabBehavior
            initiallyHidden={!isAuthenticated}
            orientation={isMobileDevice ? 'horizontal' : 'right'}
            tabHeight={60}
            elevate={false}
            variant="attendee"
            {...tabBarProps}
            {...containerConfigBySession[safeTypeContainer]}
        />
    </TransitionWithUnmount>;
};

export default SessionCollaboration;
